import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { getNearbyTowns } from '../../lib/geolocation';
import mascotImg from '/assets/mascot.jpg';
import abstractBg from '/assets/abstract-bg.svg';

// Default towns (fallback)
const defaultTowns = [
  'Chattanooga',
  'Knoxville',
  'Nashville',
  'Red Bank',
  'Hixson',
  'Soddy Daisy',
  'Signal Mountain',
  'Lookout Mountain',
  'East Ridge',
  'Ooltewah',
  'Collegedale',
  'Cleveland',
  'Dayton',
  'Jasper',
  'Dunlap',
  'Whitwell',
  'South Pittsburg',
  'Sale Creek',
  'Ringgold',
  'Fort Oglethorpe',
];

const MotionLink = motion.create(Link);
const MotionButton = motion.button;

export default function HeroSection() {
  const [towns, setTowns] = useState(defaultTowns);
  const [townIndex, setTownIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(true);
  const { showNotification } = useNotification();

  // Get max town length for styling
  const maxTownLength = towns.reduce((max, t) => t.length > max ? t.length : max, 0);
  const widthEm = maxTownLength * 0.7 + 0.5;

  // Load location-based towns
  useEffect(() => {
    const loadLocationData = async () => {
      try {
        setIsLocationLoading(true);
        const nearbyTowns = await getNearbyTowns();
        if (nearbyTowns && nearbyTowns.length > 0) {
          setTowns(nearbyTowns);
        }
      } catch (error) {
        console.error('Error loading location data:', error);
        // Keep using default towns on error
      } finally {
        setIsLocationLoading(false);
      }
    };

    loadLocationData();
  }, []);

  // Rotate through towns
  useEffect(() => {
    const interval = setInterval(() => {
      setTownIndex((prev) => (prev + 1) % towns.length);
    }, 1800);
    return () => clearInterval(interval);
  }, [towns]);

  const handleShowNotification = () => {
    showNotification("🎉 Welcome to HollerFinds! Check out our new yard sale listings in your area.");
  };

  return (
    <section className="relative w-full min-h-[70vh] bg-[#FFF8F0] dark:bg-gray-900 pt-12 pb-12 px-4 flex flex-col lg:flex-row items-center justify-center overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={abstractBg}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#FDEFD6]/30 dark:from-gray-800/30 to-transparent" />
      </div>

      <div className="relative z-10 flex-1 flex flex-col justify-center items-center lg:items-start text-center lg:text-left min-w-0 max-w-3xl mx-auto lg:mx-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-4"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#FDEFD6] dark:bg-gray-800 text-[#4B2E18] dark:text-orange-200 text-sm font-medium">
            🎉 Just Launched: Your Neighborhood's Map for Yard Sales & Porch Finds
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-[#4B2E18] dark:text-white mb-4 leading-tight"
          style={{ fontFamily: 'DM Sans, system-ui, sans-serif' }}
        >
          Find Hidden Treasures Right Here in{' '}
          <span
            className="inline-block relative h-[1.2em]"
            style={{ width: `${widthEm}em` }}
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={towns[townIndex]}
                className="text-[#F97316] dark:text-orange-400 absolute inset-0 flex items-center"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              >
                {isLocationLoading ? "Your Area" : towns[townIndex]}
              </motion.span>
            </AnimatePresence>
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7, ease: 'easeOut' }}
          className="text-lg md:text-xl text-[#4B5563] dark:text-gray-300 mb-6 max-w-2xl"
        >
          From front porch giveaways to Saturday yard sales — HollerFinds helps you uncover the good stuff, right around the corner.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex flex-col sm:flex-row gap-4 mb-6 justify-center lg:justify-start w-full"
        >
          <MotionLink
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            to="/signup"
            className="px-8 py-3 rounded-full bg-[#F97316] text-white font-medium shadow-lg hover:shadow-orange-200/50 dark:hover:shadow-orange-900/50 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-all text-center text-sm"
          >
            Join Your Community
          </MotionLink>
          <MotionLink
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            to="/listings"
            className="px-8 py-3 rounded-full border-2 border-[#F97316] text-[#F97316] dark:text-orange-400 font-medium bg-white dark:bg-gray-800 shadow-lg hover:shadow-orange-200/50 dark:hover:shadow-orange-900/50 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-all text-center text-sm"
          >
            Explore Local Sales
          </MotionLink>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="flex items-center gap-4 text-[#4B5563] dark:text-gray-400 mb-4"
        >
          <div className="flex -space-x-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-7 h-7 rounded-full border-2 border-white dark:border-gray-800 bg-[#FDEFD6] dark:bg-gray-700"
              />
            ))}
          </div>
          <p className="text-sm">
            Built by neighbors, for neighbors — welcome to the Holler
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <MotionButton
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleShowNotification}
            className="px-4 py-2 text-sm rounded-md border border-orange-300 dark:border-orange-800 bg-[#FDEFD6] dark:bg-orange-900/30 text-[#4B2E18] dark:text-orange-200 hover:bg-[#FCE5C0] dark:hover:bg-orange-900/50 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-all"
          >
            Show Announcement
          </MotionButton>
        </motion.div>
      </div>

      {/* Mascot image with enhanced styling */}
      <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.4, duration: 0.7 }}
        className="relative z-10 flex-1 flex justify-center items-center mt-8 lg:mt-0"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        <motion.div
          animate={{
            y: isHovered ? -10 : 0,
            rotate: isHovered ? 2 : 0,
          }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          <img
            src={mascotImg}
            alt="HollerFinds Mascot"
            className="w-64 h-auto max-w-xs rounded-2xl shadow-2xl border-4 border-white dark:border-gray-800 bg-white/90 dark:bg-gray-800/90"
            style={{ objectFit: 'contain' }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
} 